// The cross-roster "+ New offer" band picker (ART-139).
//
// An offer is a PRE-HOLD SHORTLIST row and it must be born ON an artist (PO, 2026-09-02) — the scope page has
// no band context of its own, so "+ New offer" here cannot open the create form directly the way the
// per-artist dashboard's does. It picks the artist first; the caller then lands on that artist's dashboard
// with `?newOffer`, where the band context exists and the same `NewOfferModal` opens filled-in-ready.
//
// Booking-linked artists only: an offer is `offers:manage`, Booking-root, so a management- or label-only link
// is not somewhere an offer can be created. `linkType` is the reach's relationship, the same discriminator the
// booking overview aggregates on.
import { useMemo, useState } from "react";
import { Field, Modal, EmptyState } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useArtistPhotoUrl } from "./artistPhoto.js";
import styles from "./NewOfferBandPicker.module.scss";

type RosterArtistView = components["schemas"]["RosterArtistView"];

const BookingLinkType = "booking";

/** Two-letter fallback when a band has no photo — its name's initials, matching the rail's mark. */
function initials(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "—";
  return (words[0][0] + (words[1]?.[0] ?? "")).toUpperCase();
}

/** One artist row — its own component because the photo is a per-band hook (the org-lens-safe resolver). */
function PickerRow({ artist, onSelect }: { artist: RosterArtistView; onSelect: () => void }) {
  const url = useArtistPhotoUrl(artist.bandId ?? null, artist.avatarMediaId ?? null);
  return (
    <button type="button" className={styles.row} onClick={onSelect}>
      {url ? (
        <img className={styles.avatar} src={url} alt="" data-photo="" />
      ) : (
        <span className={styles.avatar} aria-hidden="true">
          {initials(artist.name)}
        </span>
      )}
      <span className={styles.name}>{artist.name}</span>
    </button>
  );
}

export interface NewOfferBandPickerProps {
  artists: RosterArtistView[];
  /** The picker serves more than offers (ART-334: the label's global To-Do picks the artist a task is for). */
  title?: string;
  /** Which links may be picked. Offers are Booking-root, so the default is `booking`; `null` admits every artist. */
  linkType?: string | null;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Called with the chosen band; the caller navigates to that artist's dashboard with `?newOffer`. */
  onSelect: (bandId: string) => void;
  onClose: () => void;
}

export function NewOfferBandPicker({
  artists,
  title = "New offer",
  linkType = BookingLinkType,
  emptyTitle = "No booking artists yet",
  emptyDescription = "Offers are created on an artist your org books. None are on this roster yet.",
  onSelect,
  onClose,
}: NewOfferBandPickerProps) {
  const [q, setQ] = useState("");

  const booking = useMemo(
    () => artists.filter((a) => a.bandId && (linkType == null || a.linkType === linkType)),
    [artists, linkType],
  );
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle === "") return booking;
    return booking.filter((a) => (a.name ?? "").toLowerCase().includes(needle));
  }, [booking, q]);

  return (
    <Modal title={title} size="sm" onClose={onClose}>
      {booking.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <>
          <Field
            label="Search artists"
            hideLabel
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search artists…"
          />
          {/* A scrollable list rather than a page: a booking roster is tens of artists, and the whole point
              is to pick one fast. An empty FILTER is its own state — the roster is not empty, this search is. */}
          <div className={styles.list} role="list">
            {filtered.length === 0 ? (
              <p className={styles.noMatch}>No artists match “{q.trim()}”.</p>
            ) : (
              filtered.map((a) => (
                <PickerRow key={a.bandId} artist={a} onSelect={() => onSelect(a.bandId!)} />
              ))
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
